import React, { useEffect, useState } from 'react';
import FontFaceObserver from 'fontfaceobserver';

interface IconProps extends React.HTMLAttributes<HTMLSpanElement> {
  name: string;
  variant?: 'outlined' | 'rounded' | 'sharp';
  size?: number;
  color?: string;
  weight?: number;
  fill?: 0 | 1;
  grade?: number;
  style?: React.CSSProperties;
}
/**
 * A customizable Material Symbols icon component.
 *
 * This component renders a Material Symbols icon with various customizable
 * properties such as size, color, weight, and style.
 *
 * To make this component work, ensure you include the following in your `index.html`:
 *
 * ```html
 * <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" />
 * <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Filled" rel="stylesheet" />
 * <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Sharp" rel="stylesheet" />
 * ```
 */
export default function Icon({
  name,
  variant = 'outlined',
  size = 24,
  weight = 400,
  fill = 0,
  grade = 0,
  style,
  className,
  ...rest
}: IconProps) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const font = new FontFaceObserver(`Material Symbols Outlined`);
    font.load().then(() => setLoaded(true));
  }, [name]);

  const internalClassName = `material-symbols-${variant} ${className ?? ''}`;

  return (
    <span
      className={internalClassName}
      style={{
        fontSize: size,
        fontVariationSettings: `'FILL' ${fill}, 'wght' ${weight}, 'GRAD' ${grade}, 'opsz' ${size}`,
        display: loaded ? 'inline-block' : 'none',
        visibility: loaded ? 'visible' : 'hidden',
        ...style,
      }}
      {...rest}
    >
      {name}
    </span>
  );
}
